import { supabase } from '../utils/supabase';
import { PLANS } from '../constants/plans';
import type { CommercialPlanStatus, RecognitionStatus } from './communityAccess';

export interface ScanQuota {
  id: string;
  user_id: string;
  company: string;
  free_scans_limit: number;
  free_scans_used: number;
  manual_scans_limit?: number | null;
  manual_scans_used?: number | null;
  total_scans_used?: number | null;
  has_unlimited?: boolean | null;
  extra_scans_remaining?: number | null;
  // extra_manual_scans_remaining deprecated but might exist in DB
  extra_manual_scans_remaining?: number | null;
  created_at?: string;
  updated_at?: string;
}

export interface EffectiveScanQuota {
  user_id: string;
  period_start: string;
  next_reset_at: string;
  days_until_reset: number;
  period_used: number;
  period_limit: number;
  scans_remaining: number;
  manual_scans_used: number;
  manual_scans_limit: number;
  total_scans_used: number;
  extra_manual_scans_remaining: number;
  extra_scans_remaining: number;
  has_unlimited: boolean;
  quota_updated_at: string | null;
  plan_id: string;
  plan_status: string;
  is_paid: boolean;
  commercial_plan: CommercialPlanStatus;
  feature_tier: 'free' | 'collector' | 'dealer';
  recognition: RecognitionStatus | null;
  effective_monthly_limit: number;
  has_consumer_entitlement: boolean;
}

export interface UserCredits {
  user_id: string;
  credits_balance: number;
  free_scans_used_this_month: number;
  last_reset_date: string;
}

export async function getQuotaStatus(userId: string, company: string): Promise<ScanQuota | null> {
  try {
    const { data, error } = await supabase
      .from('scan_quotas')
      .select('*')
      .eq('user_id', userId)
      .eq('company', company.toUpperCase())
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      console.error('[QUOTA] Error fetching quota status:', error);
      return null;
    }

    return data as ScanQuota;
  } catch (error) {
    console.error('[QUOTA] Exception fetching quota status:', error);
    return null;
  }
}

export async function getAllQuotaStatuses(userId: string): Promise<Record<string, ScanQuota>> {
  try {
    const { data, error } = await supabase.from('scan_quotas').select('*').eq('user_id', userId);

    if (error) {
      console.error('[QUOTA] Error fetching all quotas:', error);
      return {};
    }

    const quotaMap: Record<string, ScanQuota> = {};
    (data || []).forEach((quota: ScanQuota) => {
      quotaMap[quota.company] = quota;
    });

    return quotaMap;
  } catch (error) {
    console.error('[QUOTA] Exception fetching all quotas:', error);
    return {};
  }
}

export async function hasActiveSubscription(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'active')
      .gte('expires_at', new Date().toISOString())
      .limit(1);

    if (error) {
      console.error('[QUOTA] Error checking subscription:', error);
      return false;
    }

    return (data?.length || 0) > 0;
  } catch (error) {
    console.error('[QUOTA] Exception checking subscription:', error);
    return false;
  }
}

export async function getSubscriptionPlan(userId: string): Promise<string> {
  try {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('plan_id, plan_type')
      .eq('user_id', userId)
      .eq('status', 'active')
      .gte('expires_at', new Date().toISOString())
      .limit(1)
      .maybeSingle();

    if (error || !data) return 'free';

    const type = (data.plan_type || data.plan_id || '').toLowerCase();
    if (type.includes('dealer')) return 'dealer';
    if (type.includes('collector') || type.includes('unlimited')) return 'collector';
    return 'free';
  } catch (e) {
    return 'free';
  }
}

export async function getUserCredits(userId: string): Promise<UserCredits | null> {
  try {
    const { data, error } = await supabase
      .from('user_credits')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      console.error('[QUOTA] Error fetching credits:', error);
      return null;
    }
    return data as UserCredits;
  } catch (err) {
    console.error('[QUOTA] Exception fetching credits:', err);
    return null;
  }
}

export async function getEffectiveScanQuota(userId: string): Promise<EffectiveScanQuota | null> {
  try {
    const { data, error } = await supabase.rpc('get_effective_scan_quota', { p_user_id: userId });
    if (error) {
      console.error('[QUOTA] Error fetching effective quota:', error);
      return null;
    }
    return (data || null) as EffectiveScanQuota | null;
  } catch (error) {
    console.error('[QUOTA] Exception fetching effective quota:', error);
    return null;
  }
}

// "Upgrade now" — end the caller's Stripe trial immediately (charge now, unlock
// the full monthly limit). Backed by the end-trial-now edge function.
export type TrialActivationResult = {
  success: boolean;
  status?: 'paid' | 'requires_action' | 'pending' | 'failed';
  clientSecret?: string;
  error?: string;
};

export async function upgradeTrialNow(): Promise<TrialActivationResult> {
  try {
    const { data, error } = await supabase.functions.invoke('end-trial-now', { body: {} });
    if (error) {
      let message = error.message;
      try {
        const body = await (error as any).context?.json?.();
        if (body?.error) message = body.error;
      } catch { /* ignore */ }
      return { success: false, status: 'failed', error: message };
    }
    if (data?.error) return { success: false, status: 'failed', error: data.error };
    if (data?.status === 'failed') return { success: false, status: 'failed', error: data.error };
    return {
      success: true,
      status: data?.status === 'requires_action' || data?.status === 'paid' || data?.status === 'pending'
        ? data.status
        : 'pending',
      clientSecret: typeof data?.client_secret === 'string' ? data.client_secret : undefined,
    };
  } catch (e: any) {
    console.error('[QUOTA] upgradeTrialNow failed:', e);
    return { success: false, status: 'failed', error: e?.message || 'Upgrade failed' };
  }
}

export async function getQuotaSummary(userId: string): Promise<{
  hasUnlimited: boolean;
  totalFreeScansRemaining: number;
  totalFreeScansMax: number;
  totalExtraScansRemaining: number;
  // Deprecated/Merged fields
  totalExtraManualScansRemaining: number;
  companiesScanned: number;
}> {
  const effective = await getEffectiveScanQuota(userId);

  if (effective) {
    const totalExtra = (effective.extra_scans_remaining || 0) + (effective.extra_manual_scans_remaining || 0);
    return {
      hasUnlimited: effective.has_unlimited,
      totalFreeScansRemaining: Math.max(0, effective.scans_remaining - totalExtra),
      totalFreeScansMax: effective.period_limit,
      totalExtraScansRemaining: totalExtra,
      totalExtraManualScansRemaining: 0,
      companiesScanned: 0
    };
  }

  const planType = await getSubscriptionPlan(userId);
  let monthlyLimit = PLANS.FREE.limits.verifications;
  if (planType === 'collector') monthlyLimit = PLANS.COLLECTOR.limits.verifications;
  if (planType === 'dealer') monthlyLimit = PLANS.DEALER.limits.verifications;

  return {
    hasUnlimited: false,
    totalFreeScansRemaining: monthlyLimit,
    totalFreeScansMax: monthlyLimit,
    totalExtraScansRemaining: 0,
    totalExtraManualScansRemaining: 0,
    companiesScanned: 0
  };
}

/**
 * Calculates usage based on actual scan history and subscription period.
 */
export function calculatePeriodUsage(
  scans: any[],
  subscription: { started_at?: string; created_at?: string } | null,
  userCredits: { last_reset_date: string } | null
): { aiUsed: number; manualUsed: number; totalUsed: number } {
  const now = new Date();
  let periodStart = new Date();

  // ... (Keep existing date logic or simplify if always monthly reset)
  if (subscription && (subscription.started_at || subscription.created_at)) {
    const startDate = new Date(subscription.started_at || subscription.created_at || now.toISOString());
    const checkDate = new Date(startDate);
    const currentMonthDate = new Date(now.getFullYear(), now.getMonth(), startDate.getDate());

    if (currentMonthDate > now) {
      periodStart = new Date(now.getFullYear(), now.getMonth() - 1, startDate.getDate());
    } else {
      periodStart = currentMonthDate;
    }
    if (periodStart < startDate) periodStart = startDate;
    if (periodStart > now) periodStart = now;

  } else if (userCredits) {
    periodStart = new Date(userCredits.last_reset_date);
  } else {
    periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
  }

  const periodScans = scans.filter(s => {
    if (!s.scan_date && !s.created_at) return false;
    const d = new Date(s.scan_date || s.created_at);
    return d >= periodStart;
  });

  // UNIFIED: All scans count as 1 usage
  return {
    aiUsed: periodScans.length, // Just return total as 'aiUsed' or unified
    manualUsed: 0,
    totalUsed: scans.length
  };
}
